import { useEffect, useCallback } from "react";
import { useSettingsStore } from "../stores/settingsStore";

type Theme = "light" | "dark" | "system";
type ResolvedTheme = "light" | "dark";

/**
 * Hook for managing the YambPro theme system.
 *
 * Supports three modes:
 * - "light": Always use light theme (warm stone backgrounds)
 * - "dark": Always use dark theme (rich stone-950 backgrounds)
 * - "system": Follow the user's OS preference
 *
 * The hook manages:
 * - Adding/removing the "dark" class on <html>
 * - Listening for system preference changes
 * - Persisting user preference via settings store
 */
export function useTheme() {
  const theme = useSettingsStore((state) => state.theme);
  const setTheme = useSettingsStore((state) => state.setTheme);

  // Resolve the actual theme based on setting and system preference
  const getResolvedTheme = useCallback((): ResolvedTheme => {
    if (theme === "system") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
    }
    return theme;
  }, [theme]);

  // Apply theme to DOM
  const applyTheme = useCallback((resolvedTheme: ResolvedTheme) => {
    const root = document.documentElement;

    if (resolvedTheme === "dark") {
      root.classList.add("dark");
      root.setAttribute("data-theme", "dark");
    } else {
      root.classList.remove("dark");
      root.setAttribute("data-theme", "light");
    }

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      // Amber-500 for light, stone-900 for dark
      metaThemeColor.setAttribute(
        "content",
        resolvedTheme === "dark" ? "#1C1917" : "#F59E0B"
      );
    }
  }, []);

  // Effect to apply theme on mount and when theme changes
  useEffect(() => {
    const resolvedTheme = getResolvedTheme();
    applyTheme(resolvedTheme);

    // Listen for system preference changes when in system mode
    if (theme === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

      const handleChange = (e: MediaQueryListEvent) => {
        applyTheme(e.matches ? "dark" : "light");
      };

      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
    return undefined;
  }, [theme, getResolvedTheme, applyTheme]);

  // Cycle through themes: light -> dark -> system -> light
  const cycleTheme = useCallback(() => {
    const order: Theme[] = ["light", "dark", "system"];
    const currentIndex = order.indexOf(theme);
    const nextIndex = (currentIndex + 1) % order.length;
    const nextTheme = order[nextIndex];
    if (nextTheme) {
      setTheme(nextTheme);
    }
  }, [theme, setTheme]);

  // Toggle between light and dark (ignores system)
  const toggleTheme = useCallback(() => {
    const resolved = getResolvedTheme();
    setTheme(resolved === "dark" ? "light" : "dark");
  }, [getResolvedTheme, setTheme]);

  return {
    /** Current theme setting: "light", "dark", or "system" */
    theme,
    /** Actual applied theme after resolving "system" */
    resolvedTheme: getResolvedTheme(),
    /** Set a specific theme */
    setTheme,
    /** Cycle through light -> dark -> system */
    cycleTheme,
    /** Quick toggle between light and dark */
    toggleTheme,
    /** Whether dark mode is currently active */
    isDark: getResolvedTheme() === "dark",
  };
}

export type { Theme, ResolvedTheme };
